import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BookUser, FileText, CalendarDays, Newspaper, Send, ArrowRight } from "lucide-react";
import IntranetSearch from "./IntranetSearch";
import ActivityFormModal from "@/components/activities/ActivityFormModal";
import { ALaUne, EventsBlock, CommsBlock, TeamBlock } from "./HomeBlocks";
import { WasaGramBlock, CompanyBlock, MovementStrip } from "./HomeBlocks2";
import NewsModal from "./NewsModal";

export default function IntranetHome({ go }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [askOpen, setAskOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [home, setHome] = useState(null);
  const [openNews, setOpenNews] = useState(null);

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || [])).catch(() => {});
    api.get("/sectors").then(r => setSectors(r.data || [])).catch(() => {});
    api.get("/intranet-home").then(r => setHome(r.data)).catch(() => {});
  }, []);

  const showNews = async (n) => {
    setOpenNews(n);
    try {
      const { data } = await api.post(`/intranet-news-view/${n.id}`);
      setHome(h => h && { ...h, news: h.news.map(x => x.id === n.id ? { ...x, views: data.views } : x) });
      setOpenNews(x => x && { ...x, views: data.views });
    } catch {}
  };

  const shortcuts = [
    { key: "annuaire", icon: BookUser, title: "Annuaire", sub: "Trouvez un collaborateur", tone: "bg-[#e0e9ff] text-[#1d4ed8]", onClick: () => go("annuaire") },
    { key: "documents", icon: FileText, title: "Documents utiles", sub: "Accédez aux ressources", tone: "bg-[#ede9fe] text-[#6d28d9]", onClick: () => navigate("/documents") },
    { key: "calendrier", icon: CalendarDays, title: "Calendrier Groupe", sub: "Tous les événements", tone: "bg-[#ffedd5] text-[#c2410c]", onClick: () => go("vie") },
    { key: "wasagram", icon: Newspaper, title: "WasaGram", sub: "La vie du réseau", tone: "bg-[#dcfce7] text-[#15803d]", onClick: () => go("wasagram") },
    { key: "demande", icon: Send, title: "Faire une demande", sub: "Contactez un service", tone: "bg-[#0b1b3a] text-white", onClick: () => setAskOpen(true) },
  ];

  return (
    <div className="space-y-6" data-testid="intranet-home">
      <section className="relative rounded-lg text-white" style={{ background: "#0b1b3a" }} data-testid="intranet-hero">
        <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none hidden md:block">
          <img src="/intranet/hero-food.jpg" alt="Good Food Good People" className="absolute inset-y-0 right-0 h-full w-[44%] object-cover" data-testid="intranet-hero-food"
               style={{ maskImage: "linear-gradient(to right, transparent, black 35%)", WebkitMaskImage: "linear-gradient(to right, transparent, black 35%)" }}/>
          <div className="absolute right-8 top-1/2 -translate-y-1/2 -rotate-3 bg-white/95 text-[#0b1b3a] px-5 py-3 rounded-sm shadow-2xl border-4 border-white" data-testid="intranet-hero-slogan">
            <div className="font-black leading-none tracking-tight text-2xl lg:text-3xl" style={{ fontFamily: "'Manrope', 'Inter', sans-serif", letterSpacing: "-0.03em" }}>
              <span className="block">Good Food</span>
              <span className="block text-[#f97316] italic" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 700 }}>Good People</span>
            </div>
          </div>
        </div>
        <div className="relative px-6 sm:px-10 py-10 sm:py-12 max-w-3xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#93c5fd]">Intranet Orbit</div>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">L'espace de vie <span className="text-[#60a5fa]">du Groupe</span></h1>
          <p className="mt-2 text-sm italic text-slate-400" data-testid="intranet-hero-quote">« Ensemble, on fait grandir bien plus que des restaurants. »</p>
          <p className="mt-3 text-sm sm:text-base text-slate-300">Nos équipes · Nos projets · Nos actualités · Nos ressources</p>
          <div className="mt-6">
            <IntranetSearch/>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="intranet-shortcuts">
        {shortcuts.map(s => (
          <button key={s.key} onClick={s.onClick} data-testid={`intranet-shortcut-${s.key}`}
                  className="group text-left bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3 hover:border-[#1d4ed8] hover:shadow-md transition-[border-color,box-shadow,transform] hover:-translate-y-0.5">
            <div className={`w-11 h-11 rounded-lg grid place-items-center shrink-0 ${s.tone}`}><s.icon className="w-5 h-5"/></div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-[#0b1b3a] leading-tight">{s.title}</div>
              <div className="text-xs text-slate-500 leading-tight mt-0.5">{s.sub}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#1d4ed8] group-hover:translate-x-0.5 transition-[color,transform]"/>
          </button>
        ))}
      </section>

      <ALaUne news={home?.news} vision={home?.vision} headcount={home?.headcount} onOpen={showNews} onAll={() => go("actualites")}/>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="intranet-row3">
        <EventsBlock events={home?.events} onAll={() => go("vie")}/>
        <CommsBlock comms={home?.comms} onAll={() => go("actualites")}/>
        <TeamBlock team={home?.team} onAll={() => go("vie")}/>
      </section>

      <WasaGramBlock data={home?.wasagram} onOpen={() => go("wasagram")}/>
      <CompanyBlock data={home?.company} go={go} navigate={navigate} onAsk={() => setAskOpen(true)}/>
      <MovementStrip items={home?.movement} go={go} onNews={(id) => { const n = home?.news?.find(x => x.id === id); if (n) showNews(n); }}/>

      <NewsModal news={openNews} onClose={() => setOpenNews(null)}/>
      <ActivityFormModal open={askOpen} onClose={() => setAskOpen(false)} onSaved={() => setAskOpen(false)}
                         stores={stores} sectors={sectors} userRole={user?.role}/>
    </div>
  );
}
